/**
 * AdminDocumentTypeTemplates.js
 *
 * Admin subtab for managing document type preset templates.
 * Presets define named bundles of enabled document types that can be
 * loaded into a company's Document Types tab.
 *
 * Data lives in globalSettings/templates.documentTypePresets (keyed by slug).
 *
 * See: docs/features/infrastructure/organizational-management.md#document-type-templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useUnsavedChanges } from './useUnsavedChanges';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import { Plus, Trash2, Star, Copy, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../utils/authContext';

/** Generate a URL-safe slug from a label */
const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const AdminDocumentTypeTemplates = ({ onDirtyChange }) => {
  const { currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Preset data
  const [presets, setPresets] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const currentSnapshot = JSON.stringify(presets);
  const hasUnsavedChanges = !isLoading && savedSnapshot !== null && currentSnapshot !== savedSnapshot;
  useUnsavedChanges(hasUnsavedChanges);
  useEffect(() => { onDirtyChange?.(hasUnsavedChanges); }, [hasUnsavedChanges, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  const [expandedPreset, setExpandedPreset] = useState(null);

  // All document types (fetched from Firestore)
  const [allDocumentTypes, setAllDocumentTypes] = useState([]);
  const [docTypesLoading, setDocTypesLoading] = useState(false);

  // New preset dialog
  const [newPresetDialog, setNewPresetDialog] = useState(false);
  const [newPresetLabel, setNewPresetLabel] = useState('');

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, key: null, label: '' });

  // ------------------------------------------
  // Load
  // ------------------------------------------
  const loadPresets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db, 'globalSettings', 'templates'));
      if (snap.exists()) {
        const loadedPresets = snap.data().documentTypePresets || {};
        setPresets(loadedPresets);
        setSavedSnapshot(JSON.stringify(loadedPresets));
      } else {
        setPresets({});
        setSavedSnapshot(JSON.stringify({}));
      }
    } catch (err) {
      console.error('Error loading document type presets:', err);
      setError('Failed to load document type presets');
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadPresets();
    const fetchDocTypes = async () => {
      setDocTypesLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'documentTypes'));
        const types = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        types.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setAllDocumentTypes(types);
      } catch (err) {
        console.error('Error fetching document types:', err);
      }
      setDocTypesLoading(false);
    };
    fetchDocTypes();
  }, [loadPresets]);

  // ------------------------------------------
  // Save
  // ------------------------------------------
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await setDoc(
        doc(db, 'globalSettings', 'templates'),
        {
          documentTypePresets: presets,
          updatedAt: new Date(),
          updatedBy: currentUser?.email || 'unknown',
        },
        { merge: true },
      );
      setSavedSnapshot(currentSnapshot);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving document type presets:', err);
      setError('Failed to save document type presets');
    }
    setIsSaving(false);
  };

  // ------------------------------------------
  // Preset helpers
  // ------------------------------------------
  const updatePreset = (presetKey, path, value) => {
    setPresets((prev) => {
      const preset = { ...prev[presetKey] };
      if (path.length === 1) {
        preset[path[0]] = value;
      } else if (path.length === 2) {
        preset[path[0]] = { ...preset[path[0]], [path[1]]: value };
      }
      return { ...prev, [presetKey]: preset };
    });
  };

  const setDefaultPreset = (presetKey) => {
    setPresets((prev) => {
      const updated = {};
      for (const [k, v] of Object.entries(prev)) {
        updated[k] = { ...v, isDefault: k === presetKey };
      }
      return updated;
    });
  };

  const duplicatePreset = (sourceKey) => {
    const source = presets[sourceKey];
    const newLabel = `${source.label} (Copy)`;
    const newKey = slugify(newLabel) || `copy-${Date.now()}`;
    setPresets((prev) => ({
      ...prev,
      [newKey]: {
        ...JSON.parse(JSON.stringify(source)),
        label: newLabel,
        isDefault: false,
        isProtected: false,
      },
    }));
    setExpandedPreset(newKey);
  };

  const deletePreset = (presetKey) => {
    setPresets((prev) => {
      const { [presetKey]: _, ...rest } = prev;
      return rest;
    });
    if (expandedPreset === presetKey) setExpandedPreset(null);
    setDeleteDialog({ open: false, key: null, label: '' });
  };

  const toggleDocType = (presetKey, typeId) => {
    setPresets((prev) => {
      const preset = { ...prev[presetKey] };
      let current = preset.enabledDocumentTypes;

      if (current === null || current === undefined) {
        current = allDocumentTypes.map(t => t.id).filter(id => id !== typeId);
      } else if (current.includes(typeId)) {
        current = current.filter(id => id !== typeId);
      } else {
        current = [...current, typeId];
      }

      if (current.length === allDocumentTypes.length) {
        current = null;
      }

      preset.enabledDocumentTypes = current;
      return { ...prev, [presetKey]: preset };
    });
  };

  const setAllDocTypes = (presetKey, selectAll) => {
    setPresets((prev) => ({
      ...prev,
      [presetKey]: {
        ...prev[presetKey],
        enabledDocumentTypes: selectAll ? null : [],
      },
    }));
  };

  const handleCreatePreset = () => {
    const label = newPresetLabel.trim();
    if (!label) return;
    const key = slugify(label) || `preset-${Date.now()}`;

    setPresets((prev) => ({
      ...prev,
      [key]: {
        label,
        isDefault: Object.keys(prev).length === 0,
        isProtected: false,
        enabledDocumentTypes: null,
        description: '',
      },
    }));
    setExpandedPreset(key);
    setNewPresetDialog(false);
    setNewPresetLabel('');
  };

  // ------------------------------------------
  // Render a single preset card
  // ------------------------------------------
  const renderPreset = (presetKey, preset) => {
    const isExpanded = expandedPreset === presetKey;
    const enabled = preset.enabledDocumentTypes;
    const enabledCount = enabled === null || enabled === undefined ? allDocumentTypes.length : enabled.length;
    const isTypeEnabled = (typeId) => enabled === null || enabled === undefined || enabled.includes(typeId);

    return (
      <Card key={presetKey} variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
              onClick={() => setExpandedPreset(isExpanded ? null : presetKey)}
            >
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <Typography variant="subtitle2">{preset.label}</Typography>
              <Chip
                label={`${enabledCount}/${allDocumentTypes.length}`}
                size="small"
                variant="outlined"
                color={enabledCount === allDocumentTypes.length ? 'success' : enabledCount === 0 ? 'error' : 'warning'}
              />
              {preset.isDefault && <Chip label="Default" size="small" color="primary" variant="outlined" />}
              {preset.isProtected && (
                <Tooltip title="Protected preset" arrow>
                  <Shield size={14} style={{ opacity: 0.5 }} />
                </Tooltip>
              )}
            </Box>
            <Tooltip title="Set as default" arrow>
              <IconButton size="small" onClick={() => setDefaultPreset(presetKey)} disabled={preset.isDefault}>
                <Star size={16} style={{ fill: preset.isDefault ? 'currentColor' : 'none' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate" arrow>
              <IconButton size="small" onClick={() => duplicatePreset(presetKey)}>
                <Copy size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={preset.isProtected ? 'Protected — cannot delete' : 'Delete'} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={preset.isProtected}
                  onClick={() => setDeleteDialog({ open: true, key: presetKey, label: preset.label })}
                >
                  <Trash2 size={16} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Expanded editor */}
          {isExpanded && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Label"
                size="small"
                fullWidth
                value={preset.label}
                onChange={(e) => updatePreset(presetKey, ['label'], e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <TextField
                label="Description"
                size="small"
                fullWidth
                value={preset.description || ''}
                onChange={(e) => updatePreset(presetKey, ['description'], e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  Enabled Document Types
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={() => setAllDocTypes(presetKey, true)}>Select All</Button>
                  <Button size="small" onClick={() => setAllDocTypes(presetKey, false)} color="warning">Clear All</Button>
                </Box>
              </Box>

              {docTypesLoading ? (
                <LinearProgress />
              ) : allDocumentTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No document types found in Firestore.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {allDocumentTypes.map((type) => (
                    <FormControlLabel
                      key={type.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={isTypeEnabled(type.id)}
                          onChange={() => toggleDocType(presetKey, type.id)}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {type.name}
                          {type.format && (
                            <Chip
                              label={type.format}
                              size="small"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem', ml: 0.5 }}
                            />
                          )}
                        </Typography>
                      }
                      sx={{ mb: -0.25 }}
                    />
                  ))}
                </Box>
              )}

              {enabled !== null && enabled !== undefined && enabled.length === 0 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This preset has no document types enabled. Companies using it will not be able to create documents.
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // ------------------------------------------
  // Main render
  // ------------------------------------------
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Document type presets saved successfully.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Document type presets define named bundles of enabled document types.
        Companies can load a preset from their Document Types tab to quickly configure which types are available.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h6">Presets</Typography>
        <Button
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => setNewPresetDialog(true)}
        >
          Add Preset
        </Button>
      </Box>

      {Object.keys(presets).length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No document type presets configured. Companies default to all types enabled.
        </Typography>
      ) : (
        Object.entries(presets).map(([key, preset]) => renderPreset(key, preset))
      )}

      {/* Save button */}
      <Box sx={{ mt: 3 }}>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Presets'}
        </Button>
      </Box>

      {/* ---- New Preset Dialog ---- */}
      <Dialog
        open={newPresetDialog}
        onClose={() => { setNewPresetDialog(false); setNewPresetLabel(''); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New Document Type Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Preset Name"
            fullWidth
            size="small"
            value={newPresetLabel}
            onChange={(e) => setNewPresetLabel(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreatePreset()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNewPresetDialog(false); setNewPresetLabel(''); }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreatePreset} disabled={!newPresetLabel.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Delete Confirmation Dialog ---- */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, key: null, label: '' })}
        maxWidth="xs"
      >
        <DialogTitle>Delete Preset</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the &quot;{deleteDialog.label}&quot; preset? This cannot be undone
            after saving.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, key: null, label: '' })}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deletePreset(deleteDialog.key)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDocumentTypeTemplates;
